/**
 * Sessie 55 blok 2 (bouwer G): het schaderegister.
 *
 * Besluit JP: "Schaderegister per klant, schadepercentage per vervoerder
 * in Gezondheid." Per vervoerder de bezorgde zendingen tegen de
 * schademeldingen in 30 of 90 dagen, daaronder de kwaliteitsklachten
 * per merk en het aantal herhalers. Een blok dat niet laadt zegt dat
 * zelf; een vervoerder zonder zendingen krijgt geen verzonnen procent.
 */
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";

import { schaderegisterApi, type SchadePeriode } from "./schaderegisterApi";

function procentTekst(procent: number | null): string {
  if (procent === null) return "geen zendingen";
  return `${procent.toLocaleString("nl-NL", { maximumFractionDigits: 1 })}%`;
}

function merkenTekst(periode: SchadePeriode): string {
  if (periode.kwaliteit_per_merk.length === 0) return "geen";
  return periode.kwaliteit_per_merk
    .map((rij) => `${rij.merk} ${rij.aantal}`)
    .join(" · ");
}

export function Schaderegister() {
  const register = useQuery({
    queryKey: ["werkplek", "gezondheid", "schaderegister"],
    queryFn: schaderegisterApi.haal,
  });
  const [gekozen, setGekozen] = useState<number | null>(null);
  const data = register.data;
  const perioden = data?.perioden ?? [];
  const periode =
    perioden.find((p) => p.dagen === gekozen) ?? perioden[0] ?? null;

  return (
    <div className="wp-blok wp-gz-mk" data-testid="schaderegister">
      <h4>
        <span>Schaderegister</span>
        {perioden.length > 1 ? (
          <span className="wp-gz-mk-onder" role="group" aria-label="Periode">
            {perioden.map((p) => (
              <button
                key={p.dagen}
                type="button"
                className="wp-knop wp-gz-sr-knop"
                aria-pressed={p.dagen === periode?.dagen}
                onClick={() => setGekozen(p.dagen)}
              >
                {p.dagen} dagen
              </button>
            ))}
          </span>
        ) : null}
      </h4>
      {register.isLoading ? (
        <p className="wp-gz-uitleg">Bezig met laden…</p>
      ) : register.isError || !data ? (
        <p className="wp-gz-uitleg" style={{ color: "var(--wp-rood)" }}>
          Het schaderegister is niet op te halen.
        </p>
      ) : data.reden_leeg || !periode ? (
        <p className="wp-gz-uitleg" style={{ color: "var(--wp-rood)" }}>
          {data.reden_leeg ?? "Het schaderegister is niet op te halen."}
        </p>
      ) : (
        <>
          <p className="wp-gz-mk-samen">
            <b>{periode.schade_totaal}</b>
            <span>
              {periode.schade_totaal === 1 ? "schademelding" : "schademeldingen"}
            </span>
            <span>
              · <b>{periode.kwaliteit_totaal}</b>{" "}
              {periode.kwaliteit_totaal === 1
                ? "kwaliteitsklacht"
                : "kwaliteitsklachten"}
            </span>
            <span>· laatste {periode.dagen} dagen</span>
          </p>
          {periode.vervoerders.length === 0 ? (
            <p className="wp-gz-uitleg" role="status">
              Geen bezorgde zendingen en geen schademeldingen in deze{" "}
              {periode.dagen} dagen.
            </p>
          ) : (
            <table className="wp-gz-mk-tabel">
              <thead>
                <tr>
                  <th>Vervoerder</th>
                  <th className="wp-gz-mk-getal">Zendingen</th>
                  <th className="wp-gz-mk-getal">Meldingen</th>
                  <th className="wp-gz-mk-getal">Percentage</th>
                </tr>
              </thead>
              <tbody>
                {periode.vervoerders.map((rij) => (
                  <tr key={rij.vervoerder}>
                    <td>{rij.vervoerder}</td>
                    <td className="wp-gz-mk-getal">{rij.zendingen}</td>
                    <td className="wp-gz-mk-getal">{rij.meldingen}</td>
                    <td className="wp-gz-mk-getal">{procentTekst(rij.procent)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <dl className="wp-gz-sr-feiten">
            <div>
              <dt>Kwaliteitsklachten per merk</dt>
              <dd data-testid="schaderegister-merken">{merkenTekst(periode)}</dd>
            </div>
            <div>
              <dt>Herhalers</dt>
              <dd data-testid="schaderegister-herhalers">
                {data.herhalers}{" "}
                {data.herhalers === 1 ? "klant" : "klanten"} met meer dan een
                melding in {data.venster_maanden} maanden
              </dd>
            </div>
          </dl>
        </>
      )}
    </div>
  );
}
